import React, { useCallback, useMemo } from "react";
import { formatCHPhone } from "../../utils/phone.js";
import { useTheme } from "../../context/ThemeContext.jsx";
import AppButton from "../ui/AppButton.jsx";
import { successButtonLoadingClassName } from "../../design/tokens.js";
import { Input as FormAtomsInput, Select, Textarea } from "../forms/shared/FormAtoms.jsx";
export default function KundenFormular({
  formData,
  onChange,
  onSubmit,
  onCancel,
  isEdit = false,
  disabled = false,
  canEditCredit = false
}) {
    // Sanfte Normalizer (werden nur clientseitig angewendet)
  const formatPLZ = useCallback(v => String(v || "").replace(/\D+/g, "").slice(0, 4), []);
  const trim = useCallback(v => typeof v === "string" ? v.trim() : v ?? "", []);

  // Kompakter Helper, der DOM-Event ODER Patch-Objekt an den Eltern weiterreicht (kompatibel)
  const passChange = useCallback(eOrPatch => {
    if (typeof onChange !== "function") return;
    onChange(eOrPatch);
  }, [onChange]);

  // Spezifische onChange-Handler für Felder mit Formatierung
  const onChangePLZ = useCallback(e => {
    const val = formatPLZ(e.target.value);
    // Patch-Objekt (verträglich zu deinen Seiten-Containern, die Events oder Patches akzeptieren)
    passChange({
      plz: val
    });
  }, [formatPLZ, passChange]);
  const makeOnChangeTel = useCallback(field => e => {
    const pretty = formatCHPhone(e.target.value);
    passChange({
      [field]: pretty
    });
  }, [passChange]);

  // OnBlur-Trim für Textfelder (verhindert versehentliche Leerzeichen)
  const onBlurTrim = useCallback((name, value) => passChange({
    [name]: trim(value)
  }), [passChange, trim]);
  const safeBottom = useMemo(() => "max(0.75rem, env(safe-area-inset-bottom))", []);
  const formCls = "grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-50";
  return <form onSubmit={onSubmit} className={formCls} noValidate>
      {/* Name/Firma */}
      <Input isDark={true} label="Firma" name="firma" value={formData.firma} onChange={passChange} onBlur={e => onBlurTrim("firma", e.target.value)} disabled={disabled} autoComplete="organization" />

      <div className="grid grid-cols-2 gap-4 md:col-span-1">
        <Input isDark={true} label="Vorname" name="vorname" value={formData.vorname} onChange={passChange} onBlur={e => onBlurTrim("vorname", e.target.value)} disabled={disabled} autoComplete="given-name" />
        <Input isDark={true} label="Nachname" name="nachname" value={formData.nachname} onChange={passChange} onBlur={e => onBlurTrim("nachname", e.target.value)} disabled={disabled} autoComplete="family-name" />
      </div>

      {/* Adresse */}
      <div className="md:col-span-2">
        <Input isDark={true} label="Straße" name="strasse" value={formData.strasse} onChange={passChange} onBlur={e => onBlurTrim("strasse", e.target.value)} disabled={disabled} autoComplete="street-address" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input isDark={true} label="PLZ" name="plz" value={formData.plz} onChange={onChangePLZ} disabled={disabled} inputMode="numeric" pattern="[0-9]{4}" maxLength={4} placeholder="4710" autoComplete="postal-code" />
        <Input isDark={true} label="Ort" name="ort" value={formData.ort} onChange={passChange} onBlur={e => onBlurTrim("ort", e.target.value)} disabled={disabled} autoComplete="address-level2" />
      </div>

      {/* Kontakt */}
     <Input isDark={true} label="Telefon" name="telefon" value={formData.telefon} onChange={makeOnChangeTel("telefon")} disabled={disabled} inputMode="tel" placeholder="062 ..." autoComplete="tel" />

     {/* NEU: E-Mail */}
<Input isDark={true} label="E-Mail" name="email" type="email" value={formData.email} onChange={passChange} onBlur={e => onBlurTrim("email", (e.target.value || "").toLowerCase())} disabled={disabled} inputMode="email" autoComplete="email" pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$" placeholder="name@domain.ch" />

     <Input isDark={true} label="Telefon 2 (optional)" name="telefon2" value={formData.telefon2} onChange={makeOnChangeTel("telefon2")} disabled={disabled} inputMode="tel" placeholder="062 ..." autoComplete="tel" />


      {/* Bonität (nur für Berechtigte im Edit-Modus) */}
      {isEdit && canEditCredit && <div className={`md:col-span-2 mt-2 border-t pt-3 ${"border-white/10"}`}>
          <div className="flex flex-wrap gap-4 items-start">
            <div>
              <label className={`block text-xs uppercase tracking-wide mb-1 ${"text-blue-300/70"}`}>Bonität</label>
              <Select name="bonitaet_status" value={formData.bonitaet_status ?? 1} onChange={passChange} disabled={disabled}>
                <option value={1}>Grün – OK</option>
                <option value={2}>Gelb – Vorsicht</option>
                <option value={3}>Rot – gesperrt</option>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-xs uppercase tracking-wide mb-1 ${"text-blue-300/70"}`}>Bonitäts-Notiz</label>
              <Textarea name="bonitaet_notiz" value={formData.bonitaet_notiz || ""} onChange={passChange} disabled={disabled} rows={3} placeholder="z.B. '2 Mahnungen offen, vor neuem Auftrag GL fragen'" />
            </div>
          </div>
        </div>}

      {/* Desktop-Buttons */}
      <div className="md:col-span-2 hidden md:flex justify-end gap-2 pt-2">
        <AppButton type="button" variant="secondary" onClick={onCancel} className={"!bg-white/10 hover:!bg-white/15 !border-white/15 px-5 py-2"}>
          Abbrechen
        </AppButton>
        <AppButton type="submit" variant="success" disabled={disabled} className={disabled ? `px-5 py-2 ${successButtonLoadingClassName}` : "px-5 py-2"}>
          {isEdit ? "Speichern" : "Erstellen"}
        </AppButton>
      </div>

      {/* Platz für Sticky-Bar (nur zur Wahrung des Scroll-Offsets) */}
      <div className="md:col-span-2 h-20 md:h-0" />

      {/* Mobile Sticky Action-Bar */}
      <div className="fixed md:hidden inset-x-0 bottom-0 z-40 px-3 pb-3" style={{
      paddingBottom: safeBottom
    }}>
        <div className={"backdrop-blur bg-black/40 border border-white/10 rounded-2xl shadow-lg p-2"}>
          <div className="grid grid-cols-2 gap-2">
            <AppButton type="button" variant="secondary" onClick={onCancel} className={"rounded-xl px-4 py-3 active:scale-[0.98] !border-white/15 !bg-white/10 hover:!bg-white/15"}>
              Abbrechen
            </AppButton>
            <AppButton type="submit" variant="success" disabled={disabled} className={disabled ? `rounded-xl px-4 py-3 active:scale-[0.98] ${successButtonLoadingClassName}` : "rounded-xl px-4 py-3 active:scale-[0.98]"}>
              {isEdit ? "Speichern" : "Erstellen"}
            </AppButton>
          </div>
        </div>
      </div>
    </form>;
}

/* ---------- UI Atoms (leicht verfeinert) ---------- */
function Input({
  isDark = true,
  label,
  name,
  value = "",
  onChange,
  onBlur,
  type = "text",
  disabled,
  inputMode,
  pattern,
  maxLength,
  placeholder,
  autoComplete
}) {
  const labelId = `label-${name}`;
  const invalid = Boolean(pattern) && value && !new RegExp(`^${pattern}$`).test(String(value));
  const labelCls = "block text-xs uppercase tracking-wide text-blue-300/70 mb-1";
  return <div>
      <label id={labelId} htmlFor={name} className={labelCls}>{label}</label>
      <FormAtomsInput id={name} aria-labelledby={labelId} aria-invalid={invalid ? "true" : "false"} type={type} name={name} value={value || ""} onChange={e => {
      if (typeof onChange === "function") onChange(e);
    }} onBlur={onBlur} disabled={disabled} inputMode={inputMode} pattern={pattern} maxLength={maxLength} placeholder={placeholder} autoComplete={autoComplete} autoCapitalize="none" autoCorrect="off" className={invalid ? "ring-2 ring-red-500/40" : ""} />
      {invalid && <p className={"mt-1 text-[11px] text-red-300/90"}>
          Eingabeformat stimmt nicht ({label}{maxLength ? `, max ${maxLength} Zeichen` : ""}).
        </p>}
    </div>;
}